import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { mondayOf, formatRange, dayCells, statusLabel, todayPT, entryHours } from "@/lib/util";
import { usePhases } from "@/lib/phases";
import { AppHeader } from "@/components/AppHeader";
import { BottomTabs } from "@/components/BottomTabs";
import { StatusBadge } from "@/components/StatusBadge";
import LoadError from "@/components/LoadError";
import DailyEntrySheet from "@/pages/crew/DailyEntrySheet";
import SubmitConfirmDialog from "@/pages/crew/SubmitConfirmDialog";
import { CalendarDays, Megaphone, ChevronLeft, ChevronRight, Lock, Send, Check, ShieldCheck } from "lucide-react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SafetyMeetings from "@/pages/safety/SafetyMeetings";

function WeekHeader({ payPeriod, year, range, deadline, locked, totals }) {
    const dl = dayjs(deadline);
    const worked = totals?.total || 0;
    return (
        <div className="space-y-2" data-testid="week-header">
            <div className="bg-brand-navy text-brand-bone rounded-2xl p-5 shadow-lift">
                <div className="flex justify-between items-start">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Pay Period</div>
                        <div className="font-bold text-3xl leading-tight mt-1" data-testid="pay-period-number">
                            {payPeriod} <span className="text-brand-gold/80 text-xl">of 52</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Year</div>
                        <div className="font-serif font-bold text-2xl mt-1">{year}</div>
                    </div>
                </div>
                <div className="mt-3 text-sm font-medium" data-testid="week-range">{range}</div>
                <div className="mt-3 pt-3 border-t border-brand-bone/20 flex gap-8">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Hours so far</div>
                        <div className="font-serif font-bold text-2xl mt-0.5" data-testid="header-hours-worked">{worked.toFixed(2)}h</div>
                    </div>
                </div>
            </div>
            <div className={`px-4 py-2.5 rounded-xl flex items-center justify-between text-sm font-bold ${locked ? "bg-brand-charcoal text-brand-bone" : "bg-brand-gold text-brand-charcoal"}`} data-testid="deadline-strip">
                <span className="flex items-center gap-2">
                    {locked ? <Lock className="h-4 w-4" /> : <CalendarDays className="h-4 w-4" />}
                    {locked ? "Week locked" : "Add hours daily · send in by Sunday 11:59 PM"}
                </span>
                <span className="text-xs font-medium opacity-80">{dl.format("ddd MMM D · h:mm A")}</span>
            </div>
        </div>
    );
}

function DayStrip({ weekStart, entries, selectedDate, onSelect }) {
    const cells = useMemo(() => dayCells(weekStart), [weekStart]);
    const hoursByDay = useMemo(() => {
        const map = {};
        entries.forEach((e) => {
            map[e.date] = (map[e.date] || 0) + entryHours(e);
        });
        return map;
    }, [entries]);
    return (
        <div className="flex gap-3 overflow-x-auto py-4 hide-scrollbar snap-x" data-testid="day-strip">
            {cells.map((c) => {
                const active = c.date === selectedDate;
                const h = hoursByDay[c.date] || 0;
                let done = false;
                try { done = localStorage.getItem(`talon.daydone.${c.date}`) === "1"; } catch (e) { /* ignore */ }
                return (
                    <button
                        key={c.date}
                        onClick={() => onSelect(c.date)}
                        data-testid={`day-cell-${c.dow.toLowerCase()}`}
                        className={`relative snap-center flex flex-col items-center justify-center min-w-[78px] h-24 rounded-xl transition-all border ${
                            active
                                ? "bg-brand-charcoal text-brand-bone border-brand-gold shadow-lift scale-105"
                                : "bg-white text-brand-charcoal border-brand-charcoal/15 hover:border-brand-navy/40"
                        }`}
                    >
                        {done && (
                            <span className="absolute top-1.5 right-1.5 text-brand-success" data-testid={`day-done-${c.dow.toLowerCase()}`} title="Marked done for the day">
                                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            </span>
                        )}
                        <span className={`text-[10px] uppercase tracking-widest ${active ? "text-brand-gold" : "text-brand-navy"}`}>{c.dow}</span>
                        <span className="font-serif font-bold text-2xl leading-none mt-1">{c.dayNum}</span>
                        <span className={`mt-1 text-[11px] font-bold ${active ? "text-brand-bone" : "text-brand-charcoal/70"}`}>
                            {h > 0 ? `${h.toFixed(1)}h` : "—"}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function SummaryCard({ totals, projects = [] }) {
    // One number, then where it went. This version records worked hours and
    // nothing else -- no overtime -- so the row of figures that used to sit
    // above the total had shrunk to a single box repeating the total
    // underneath it. The split by job is the part a crew member can actually
    // check: "I was on Waters Thursday, so why is it all Reddick?"
    //
    // PTO breaks that single-number story back open, deliberately: it isn't
    // paid until the office checks it against an accrued balance, so folding
    // it into "Total hours" the way a regular job's hours are would say more
    // than the app actually knows. Shown only when PTO was actually logged,
    // so the common case -- nobody used it this week -- still reads as one
    // number.
    const byProject = totals?.by_project || {};
    const names = Object.fromEntries(projects.map((p) => [p.project_id, p.name]));
    const ptoIds = new Set(projects.filter((p) => p.pto_kind).map((p) => p.project_id));
    const rows = Object.entries(byProject).filter(([pid, h]) => h > 0 && !ptoIds.has(pid));
    const pto = totals?.pto || 0;
    const regular = totals?.regular ?? (totals?.total || 0) - pto;
    return (
        <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5" data-testid="weekly-summary">
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-navy mb-3">Weekly Summary</div>
            {pto > 0 ? (
                <div className="space-y-2">
                    <div className="flex justify-between items-end">
                        <div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Regular paid hours</div>
                        <div className="font-bold text-2xl text-brand-navy tabular-nums" data-testid="summary-regular">{regular.toFixed(2)}</div>
                    </div>
                    <div className="flex justify-between items-end">
                        <div className="text-xs uppercase tracking-widest text-brand-charcoal/60">PTO hours</div>
                        <div className="font-bold text-2xl text-brand-charcoal tabular-nums" data-testid="summary-pto">{pto.toFixed(2)}</div>
                    </div>
                    <p className="text-[11px] text-brand-charcoal/50 leading-snug">
                        PTO isn't guaranteed — it depends on your accrued balance, checked by the office before payroll.
                    </p>
                    <div className="flex justify-between items-end pt-2 border-t border-brand-charcoal/10">
                        <div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Total hours</div>
                        <div className="font-bold text-xl text-brand-charcoal tabular-nums" data-testid="summary-total">{(totals?.total || 0).toFixed(2)}</div>
                    </div>
                </div>
            ) : (
                <div className="flex justify-between items-end">
                    <div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Total hours</div>
                    <div className="font-bold text-3xl text-brand-navy tabular-nums" data-testid="summary-total">{(totals?.total || 0).toFixed(2)}</div>
                </div>
            )}
            {rows.length > 0 && (
                <div className="mt-4 pt-4 border-t border-brand-charcoal/10 space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-brand-navy mb-1">By job</div>
                    {rows.map(([pid, h]) => (
                        <div key={pid} className="flex justify-between text-sm" data-testid={`summary-project-${pid}`}>
                            <span className="text-brand-charcoal/80">{names[pid] || "—"}</span>
                            <span className="tabular-nums font-semibold">{(h || 0).toFixed(2)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** One person's own week: the day strip, the entry sheet, the certification.
 *
 *  Exported because foremen work too. They had no way to log their own hours at
 *  all -- the foreman app opens on other people's weeks -- and the fix is this
 *  screen under their own tab rather than a second entry UI that would drift
 *  from this one the first time either changed.
 */
export function ThisWeek() {
    const { phaseName } = usePhases();
    const navigate = useNavigate();
    // Always open on the CURRENT week. This used to restore the last week the
    // crew member had viewed, which meant anyone who glanced back at a previous
    // week -- or simply used the app last week -- reopened it on a stale week
    // and logged hours against it. Going back is a deliberate act (the < arrow),
    // never the default. Browsing history is not worth a misfiled timecard.
    const [weekStart, setWeekStart] = useState(() => mondayOf(todayPT()));
    const [data, setData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [openSheet, setOpenSheet] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    // Fetched here rather than in the day sheet so the week list can name the
    // job on each entry, and so opening a day doesn't re-request the list.
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        // api is a stable module-level singleton — omitting from deps is intentional
        // eslint-disable-next-line react-hooks/exhaustive-deps
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    }, []);

    // api is a stable module-level singleton — omitting from deps is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const load = useCallback(async (ws) => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get(`/timecards/me?week=${ws}`);
            setData(r.data);
            setSelectedDate((prev) => prev || ws);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to load week.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(weekStart); }, [weekStart, load]);

    const changeWeek = (delta) => {
        const next = dayjs(weekStart).add(delta * 7, "day").format("YYYY-MM-DD");
        if (dayjs(next).isAfter(todayPT(), "day")) return; // can't go future
        setWeekStart(next);
        setSelectedDate(next);
    };

    const entries = data?.entries;
    const entriesByDay = useMemo(() => {
        if (!entries) return [];
        const grouped = {};
        [...entries].sort((a, b) => a.date.localeCompare(b.date)).forEach((e) => {
            (grouped[e.date] = grouped[e.date] || []).push(e);
        });
        return Object.keys(grouped).sort().map((date) => ({
            date,
            items: grouped[date],
            dayTotal: grouped[date].reduce((sum, e) => sum + entryHours(e), 0),
        }));
    }, [entries]);

    if (loading && !data) {
        return <div className="p-8 text-center text-brand-charcoal/70" data-testid="loading">Loading…</div>;
    }
    // `err` used to be set here and never rendered, so a failed week load left
    // the crew member staring at a blank screen with no way to retry.
    if (!data) {
        return <LoadError message={err} onRetry={() => load(weekStart)} testId="crew-week-error" />;
    }

    const { timecard, deadline, pay_period, year, can_edit } = data;
    const range = formatRange(weekStart);

    // The dialog collects the certification; this just carries it to the server.
    // Errors are handed back to the dialog so it can stay open with the reason
    // showing, rather than closing and losing what the crew member typed.
    const handleSubmit = async (certification) => {
        await api.post(`/timecards/${timecard.timecard_id}/submit`, certification);
        setConfirmSubmit(false);
        await load(weekStart);
    };

    return (
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-4 pb-28" data-testid="this-week-page">
            <div className="flex items-center justify-between">
                <button onClick={() => changeWeek(-1)} className="p-2 rounded-lg hover:bg-brand-stone" data-testid="prev-week">
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2">
                    <StatusBadge status={timecard.status} />
                </div>
                <button
                    onClick={() => changeWeek(1)}
                    disabled={dayjs(weekStart).add(7, "day").isAfter(todayPT(), "day")}
                    className="p-2 rounded-lg hover:bg-brand-stone disabled:opacity-30"
                    data-testid="next-week"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>

            <WeekHeader payPeriod={pay_period} year={year} range={range} deadline={deadline} locked={!can_edit} totals={timecard.totals} />

            {timecard.manager_note && (
                <div className="bg-brand-warn/15 border border-brand-warn/40 rounded-xl p-3 text-sm text-brand-charcoal" data-testid="manager-note">
                    <div className="font-bold text-[10px] uppercase tracking-widest text-brand-charcoal mb-1">Note from foreman</div>
                    {timecard.manager_note}
                </div>
            )}

            {timecard.status === "submitted" && can_edit && (
                <div className="bg-brand-success/10 border border-brand-success/30 rounded-xl p-3 text-sm text-brand-success" data-testid="submitted-but-editable-banner">
                    <b>Submitted to your foreman.</b> You can keep adding entries below — they'll be picked up next time the foreman reviews. The week locks once it's approved.
                </div>
            )}

            <DayStrip weekStart={weekStart} entries={entries} selectedDate={selectedDate || weekStart} onSelect={(d) => { setSelectedDate(d); setOpenSheet(true); }} />

            <SummaryCard totals={timecard.totals} projects={projects} />

            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5" data-testid="entries-list">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-navy">This week's entries</div>
                    {can_edit && (
                        <button
                            onClick={() => { setSelectedDate(selectedDate || weekStart); setOpenSheet(true); }}
                            className="text-sm font-bold text-brand-navy hover:text-brand-navy-dark"
                            data-testid="add-entry-button"
                        >
                            + Add entry
                        </button>
                    )}
                </div>
                {entries.length === 0 ? (
                    <div className="text-sm text-brand-charcoal/60 py-6 text-center" data-testid="no-entries">
                        No entries yet.<br />
                        <span className="text-brand-navy font-semibold">Tap a day above</span> or
                        <button onClick={() => { setSelectedDate(selectedDate || weekStart); setOpenSheet(true); }} className="text-brand-navy font-semibold underline ml-1" data-testid="empty-state-add">tap here</button>
                        {" "}to add time for a client.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {entriesByDay.map((day) => (
                            <div key={day.date} data-testid={`day-group-${day.date}`}>
                                <div className="flex items-center justify-between bg-brand-stone rounded-lg px-3 py-2">
                                    <span className="text-xs text-brand-navy uppercase tracking-widest font-bold">{dayjs(day.date).format("ddd MMM D")}</span>
                                    <span className="text-sm font-bold text-brand-charcoal" data-testid={`day-total-${day.date}`}>{day.dayTotal.toFixed(2)}h</span>
                                </div>
                                <ul className="divide-y divide-brand-charcoal/10">
                                    {day.items.map((e) => (
                                        <li key={e.entry_id} className="py-3 px-1 flex items-center justify-between gap-3" data-testid={`entry-${e.entry_id}`}>
                                            <div className="min-w-0">
                                                {/* Lead with the job. Two entries on the same day were
                                                    previously distinguishable only by their times, which
                                                    is no help when checking a week against memory. */}
                                                {(() => {
                                                    const proj = projects.find((p) => p.project_id === e.project_id);
                                                    if (!proj) return null;
                                                    return (
                                                        <div className="flex items-baseline gap-2">
                                                            <span className="text-sm font-bold text-brand-charcoal truncate" data-testid={`entry-job-${e.entry_id}`}>{proj.name}</span>
                                                            {proj.job_number && !proj.pto_kind && <span className="text-[10px] text-brand-charcoal/50 shrink-0">{proj.job_number}</span>}
                                                        </div>
                                                    );
                                                })()}
                                                {/* The phases, by name, so a week can be checked against
                                                    memory without opening every day. PTO has none. */}
                                                <div className="text-sm text-brand-charcoal/80" data-testid={`entry-phase-${e.entry_id}`}>
                                                    {(e.phase_numbers || []).map((n) => phaseName(n)).join(" · ") || "—"}
                                                </div>
                                                {e.notes && <div className="text-xs text-brand-charcoal/60 truncate italic">"{e.notes}"</div>}
                                            </div>
                                            <button
                                                onClick={() => { setSelectedDate(e.date); setOpenSheet(true); }}
                                                className="text-xs font-semibold text-brand-navy hover:underline"
                                                data-testid={`edit-entry-${e.entry_id}`}
                                            >
                                                Edit
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {can_edit && entries.length > 0 && (
                <div className="space-y-2">
                    <button
                        onClick={() => setConfirmSubmit(true)}
                        data-testid="submit-timecard-button"
                        className="w-full h-14 bg-brand-navy hover:bg-brand-navy-dark text-brand-bone rounded-xl font-bold text-base flex items-center justify-center gap-2 shadow-card transition-colors"
                    >
                        <Send className="h-5 w-5" />
                        Send my week to the foreman
                    </button>
                    <p className="text-xs text-brand-charcoal/60 text-center" data-testid="submit-reassurance">
                        You can still add or fix hours after sending — until your foreman approves.
                    </p>
                </div>
            )}

            {openSheet && (
                <DailyEntrySheet
                    projects={projects}
                    timecard={timecard}
                    weekStart={weekStart}
                    date={selectedDate || weekStart}
                    entries={entries.filter((e) => e.date === (selectedDate || weekStart))}
                    canEdit={can_edit}
                    onClose={() => setOpenSheet(false)}
                    onChanged={() => load(weekStart)}
                />
            )}

            {confirmSubmit && (
                <SubmitConfirmDialog
                    totals={timecard.totals}
                    entries={entries}
                    projects={projects}
                    range={range}
                    onCancel={() => setConfirmSubmit(false)}
                    onConfirm={handleSubmit}
                />
            )}
        </div>
    );
}

export default function CrewApp() {
    const [unwatched, setUnwatched] = useState(0);
    useEffect(() => {
        const fetch = () => api.get("/safety/unwatched-count").then((r) => setUnwatched(r.data.count || 0)).catch(() => {});
        fetch();
        const timer = setInterval(fetch, 60000);
        return () => clearInterval(timer);
    }, []);
    return (
        <div className="min-h-screen paper-bg flex flex-col">
            <AppHeader />
            <main className="flex-1">
                <Routes>
                    <Route index element={<ThisWeek />} />
                    <Route path="safety" element={<SafetyMeetings />} />
                    <Route path="announcements" element={<AnnouncementsPage />} />
                </Routes>
            </main>
            <BottomTabs
                tabs={[
                    { to: "/crew", label: "This Week", icon: CalendarDays, end: true },
                    { to: "/crew/safety", label: "Safety", icon: ShieldCheck, badge: unwatched },
                    { to: "/crew/announcements", label: "News", icon: Megaphone },
                ]}
            />
        </div>
    );
}
